import type { ColorName } from '@emotion/react';
import {
  type EncounterService,
  GroupSecurity,
  getPeriodDayWithPausePeriod,
} from '@quromedical/fhir-common';
import type { AdmissionGetResponse } from '@quromedical/models/admission';

import type { MovePatientsToWardInput } from '@quromedical/models/wards';
import { useQuery } from '@tanstack/react-query';
import {
  type ColumnType,
  type SortConfig,
  useTableState,
} from 'components/table';
import { WithGroup } from 'core/auth';
import {
  admissionServiceDisplay,
  getDisplayDate,
  getPeriodDayDisplay,
} from 'core/display';
import { PaginatedTable } from 'core/table';
import {
  Button,
  ExtendedCard as Card,
  CheckboxWithIcon,
  Dot,
  Header as HeaderComponent,
  Skeleton,
  SkeletonProvider,
  Visible,
} from 'design-system';
import type { EncounterStatusHistory } from 'fhir/r4';
import { useUserSession } from 'hooks';
import { useFiniteState } from 'hooks/useFiniteState';
import { type FetcherOptions, getField } from 'hooks/usePaginatedData';
import { useIsBiggerThan, useIsSmallerThan } from 'hooks/useResponsive';
import { useRevalidation } from 'hooks/useRevalidation';
import { useToggle } from 'hooks/useToggle';
import { locationWardFetcher } from 'integration';
import { AdmissionApi, admissionSWRKey } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { strings } from 'strings';

import {
  ContentRow,
  ItemRowWithMargin,
  ModalBackground,
  matchWardsToLocationWard,
  TableWrapper,
  WardWrapper,
} from './internals';
// import { WardCount } from './WardCount';
import { WardSelect } from './WardSelect';

const api = new AdmissionApi();

type FormState = 'initial' | 'editing' | 'submitting' | 'error';

function getAcuityColor(acuityDisplay: string): ColorName {
  const acuityColorMap: Record<string, ColorName> = {
    'Intensive Monitoring': 'status-critical',
    'Medium Monitoring': 'accent-orange',
    'Low Monitoring': 'accent-green',
  };
  return acuityDisplay ? acuityColorMap[acuityDisplay] || 'white' : 'white';
}

const getColumns = (
  selectedIds: string[],
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
  currentPageData: AdmissionGetResponse[],
): ColumnType<AdmissionGetResponse>[] => [
  {
    accessor: 'subject',
    header: (
      <CheckboxWithIcon
        checked={
          currentPageData.length > 0 &&
          currentPageData.every(
            (row) => row.patient?.id && selectedIds.includes(row.patient.id),
          )
        }
        onChange={() => {
          const currentPagePatientIds = currentPageData
            .map((row) => row.patient?.id)
            .filter((id): id is string => typeof id === 'string');

          const allSelectedOnPage = currentPagePatientIds.every((id) =>
            selectedIds.includes(id),
          );

          if (allSelectedOnPage) {
            setSelectedIds((prev) =>
              prev.filter((id) => !currentPagePatientIds.includes(id)),
            );
          } else {
            const otherSelectedIds = selectedIds.filter(
              (id) => !currentPagePatientIds.includes(id),
            );
            setSelectedIds([...otherSelectedIds, ...currentPagePatientIds]);
          }
        }}
        color="primary"
        size={20}
        iconColor="white"
      />
    ),
    Cell: ({ row }) => {
      const patientId = row.original.patient.id;
      return (
        <CheckboxWithIcon
          checked={selectedIds.includes(patientId)}
          onChange={() => {
            setSelectedIds((prev) =>
              prev.includes(patientId)
                ? prev.filter((id) => id !== patientId)
                : [...prev, patientId],
            );
          }}
          color={selectedIds.includes(patientId) ? 'primary' : 'base-grey'}
          size={20}
          iconColor="white"
        />
      );
    },
    disableSortBy: true,
  },
  {
    accessor: 'patient',
    header: strings.LabelNameAndSurname,
    Cell: ({ value }) => value?.display || '',
  } as ColumnType<AdmissionGetResponse, 'patient'>,
  {
    accessor: 'location',
    header: strings.LabelLocation,
    Cell: ({ value }) => {
      const display = value.display;
      const color = getAcuityColor(display);

      return (
        <>
          <Dot color={color} /> {display}
        </>
      );
    },
  } as ColumnType<AdmissionGetResponse, 'location'>,
  {
    accessor: 'period',
    header: strings.AdmissionStartLabel,
    disableSortBy: false,
    Cell: ({ value }) => <>{getDisplayDate(value?.start) || ''}</>,
  } as ColumnType<AdmissionGetResponse, 'period'>,
  {
    accessor: 'period',
    id: 'daysOfService',
    header: strings.AdmissionDay,
    disableSortBy: true,
    Cell: ({ row }) => {
      const { period, statusHistory } = row.original;

      const hasPausedHistory = statusHistory?.some(
        (history: EncounterStatusHistory) => history.status === 'onleave',
      );

      const dayDisplay = hasPausedHistory
        ? getPeriodDayWithPausePeriod(
            statusHistory as EncounterStatusHistory[],
            period,
          )
        : getPeriodDayDisplay(period);

      return <>{dayDisplay || ''}</>;
    },
  } as ColumnType<AdmissionGetResponse, 'period'>,
  {
    accessor: 'service',
    header: strings.AdmissionServiceLabel,
    disableSortBy: true,
    Cell: ({ value }: { value: EncounterService }) => (
      <span>{admissionServiceDisplay[value] || ''}</span>
    ),
  } as ColumnType<AdmissionGetResponse, 'service'>,
];

const sortConfig: SortConfig<AdmissionGetResponse> = {
  period: {
    asc: 'date',
    desc: '-date',
  },
};

export const ManageWardsDialog: React.FC = () => {
  const [wardDialog, setWardDialog] = useToggle(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedWard, setSelectedWard] = useState<string>('');
  const [data, setData] = useState<AdmissionGetResponse[]>([]);
  const state = useFiniteState<FormState>('initial');
  const userSession = useUserSession();

  const revalidate = useRevalidation('trpc/admission');

  const { data: wardOptions = [] } = useQuery({
    queryKey: ['locationWards', userSession.ward],
    queryFn: () => locationWardFetcher(),
    enabled: wardDialog,
  });

  const wards = useMemo(() => {
    return matchWardsToLocationWard(userSession.wards, wardOptions);
  }, [userSession.wards, wardOptions]);

  const handleWardSelect = useCallback((wardId: string) => {
    setSelectedWard(wardId);
  }, []);

  const tableState = useTableState<AdmissionGetResponse>({ sort: sortConfig });
  const cacheKey = [admissionSWRKey, tableState.sort, userSession.ward];

  const fetcher = useCallback(
    async (options?: FetcherOptions) => {
      const response = await api.getAdmissions({
        ...options,
        pageToken: options?.nextToken,
        sort: tableState.sort,
        includeAdminAssignments: true,
        location: userSession.ward,
        status: 'in-progress',
      });
      setData(response.admissions);
      return response;
    },
    [tableState.sort, userSession.ward],
  );

  const onSubmit = useCallback(
    async (result: MovePatientsToWardInput) => {
      try {
        state.set('submitting');
        await api.moveAdmissionsToWard(result);
        await fetcher();
        setSelectedIds([]);
        revalidate();
        state.set('initial');
      } catch (err) {
        console.error(err);
        state.set('error');
      }
    },
    [fetcher, state, revalidate],
  );

  const columns = useMemo(
    () => getColumns(selectedIds, setSelectedIds, data),
    [selectedIds, data],
  );

  const numberOfAdmissions = selectedIds.length;
  const isSmallerThanMedium = useIsSmallerThan('md');
  const isSmall = useIsSmallerThan('lg');
  const isLarge = useIsBiggerThan('3xl');

  return (
    <SkeletonProvider loading={state.is('submitting')}>
      <Skeleton>
        <Visible if={!isSmallerThanMedium}>
          <Button
            variant="flat"
            text={strings.ManageWards}
            size="large"
            onPress={() => setWardDialog()}
          />

          <Visible if={wardDialog}>
            <WithGroup groups={[GroupSecurity.EncounterUpdate]}>
              <Modal transparent visible={wardDialog} animationType="fade">
                <ScrollView centerContent>
                  <ModalBackground>
                    <Card
                      maxSize={'4xl'}
                      paddingVertical={0}
                      overflow="hidden"
                      borderColor="primary"
                      border={true}
                      hasThickBorder={true}
                    >
                      <ContentRow>
                        <HeaderComponent
                          title={strings.ChangeWard}
                          icon={'person-outline'}
                          blockColor={'primary'}
                          borderColor="status-critical"
                          iconColor="white"
                          isSmall={isSmall}
                        />

                        <ItemRowWithMargin isSmall={isSmall}>
                          <Button
                            variant="outlined"
                            text={strings.ButtonTextCancel}
                            size={isSmall ? 'default' : 'large'}
                            onPress={() => {
                              setWardDialog();
                              setSelectedIds([]);
                            }}
                          />
                          <Button
                            variant="contained"
                            text={
                              state.is('submitting')
                                ? strings.DisplayLoading
                                : `${strings.Move} (${numberOfAdmissions})`
                            }
                            size={isSmall ? 'default' : 'large'}
                            disabled={
                              selectedIds.length === 0 ||
                              state.is('submitting') ||
                              selectedWard === ''
                            }
                            onPress={() =>
                              onSubmit({
                                patientIds: selectedIds,
                                wardId: selectedWard,
                              })
                            }
                          />
                          <WardSelect
                            isSmall={isSmall}
                            wards={wards}
                            selected={selectedWard}
                            onSelect={handleWardSelect}
                          />
                        </ItemRowWithMargin>
                      </ContentRow>

                      <WardWrapper>{/* <WardCount /> */}</WardWrapper>

                      <TableWrapper>
                        <PaginatedTable
                          initialCount={isLarge ? 10 : 7}
                          cacheKey={cacheKey}
                          fetcher={fetcher}
                          getDataFromResponse={getField('admissions')}
                          onTableStateChanged={tableState.onChange}
                          columns={columns}
                          showHeader={true}
                          rowType="padded-full-background"
                        />
                      </TableWrapper>
                    </Card>
                  </ModalBackground>
                </ScrollView>
              </Modal>
            </WithGroup>
          </Visible>
        </Visible>
      </Skeleton>
    </SkeletonProvider>
  );
};
